import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import {
  IChangePassword,
  IChangePasswordRequest,
  ILoginResponse,
  INormalLogin,
  IRegister,
  IRegisterRequest,
  IResetPassword,
  SocialLoginProvierType,
} from "@/integration/user/interfaces/auth.interface.ts";
import { QUERY_KEY_USER } from "@integration/factory/queryKeys.constants.ts";
import { SubmitHandler } from "react-hook-form";
import UserContextExports from "@/context/UserContext.tsx";
import ToastContextExports from "@/context/ToastContext.tsx";
import authService from "@integration/user/services/auth.service.ts";
// Removed sonner import as we're using the custom ToastContext

const useAuth = () => {
  const { isAuthenticated, isLoadingAuth } = useContext(
    UserContextExports.Context
  );
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { showToast } = ToastContextExports.useToast();

  const {
    postLoginSocial,
    postLogin,
    postRegister,
    postChangePassword,
    postResetPassword,
  } = authService();

  // Utilitário para lidar com tokens no localStorage
  const handleSetToken = (token: string | null) => {
    // updateToken(token);
    if (token) {
      localStorage.setItem("progamers.token", token);
    } else {
      localStorage.removeItem("progamers.token");
    }
  };

  // Mutations para autenticação
  const mutationRegister = useMutation<ILoginResponse, Error, IRegisterRequest>(
    {
      mutationFn: postRegister,
      onSuccess: () => {
        showToast(
          "Registro",
          "Registro de usuario efetuado com sucesso!",
          "success"
        );
        navigate("/auth");
      },
      onError: (error) => {
        showToast(
          "Registro",
          error?.message ?? "Erro ao registrar usuário!",
          "error"
        );
      },
    }
  );

  const mutationLogin = useMutation<ILoginResponse, Error, INormalLogin>({
    mutationFn: postLogin,
    onSuccess: async (response) => {
      handleSetToken(response.token);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_USER.PROFILE] });
      navigate(`/profile`);
      window.location.reload();
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const mutationChangePassword = useMutation<
    void,
    Error,
    IChangePasswordRequest
  >({
    mutationFn: postChangePassword,
    onSuccess: () =>
      showToast("Sucesso", "Senha alterada com sucesso", "success"),
  });

  const mutationResetPassword = useMutation<void, Error, IResetPassword>({
    mutationFn: postResetPassword,
    onSuccess: () =>
      showToast(
        "E-mail",
        "E-mail de recuperação de conta enviado com sucesso!",
        "success"
      ),
    onError: (error) => console.error("Erro ao redefinir senha:", error),
  });

  // Funções de login social
  const loginSocial = async (
    token: string,
    provider: SocialLoginProvierType
  ) => {
    try {
      const result = await postLoginSocial({ token, providerType: provider });
      handleSetToken(result.token);
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_USER.PROFILE],
      });
      navigate(`/profile`);
      window.location.reload();
    } catch (error) {
      console.error(`Erro no login social (${provider}):`, error);
    }
  };

  const loginSocialGoogle = (token: string) =>
    loginSocial(token, SocialLoginProvierType.GOOGLE);
  const loginSocialSteam = (token: string) =>
    loginSocial(token, SocialLoginProvierType.STEAM);

  // Handlers de login e registro
  const normalLogin: SubmitHandler<INormalLogin> = async (values, event) => {
    event?.preventDefault();

    await mutationLogin.mutateAsync(values);
  };

  const submiteRegister: SubmitHandler<IRegister> = async (values, event) => {
    event?.preventDefault();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { passwordConfirm, ...data } = values;
    await mutationRegister.mutateAsync(data);
  };

  const submiteChangePassword: SubmitHandler<IChangePassword> = async (
    values,
    event
  ) => {
    event?.preventDefault();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { passwordConfirm, ...data } = values;
    await mutationChangePassword.mutateAsync(data);
  };

  const submiterResetPassword: SubmitHandler<IResetPassword> = async (
    values,
    event
  ) => {
    event?.preventDefault();
    await mutationResetPassword.mutateAsync(values);
  };

  // Login via Steam OpenID
  const handleLoginSteam = () => {
    const returnUrl = encodeURIComponent(
      `${window.location.origin}/auth/steam/callback`
    );
    const steamUrl = `https://steamcommunity.com/openid/login?openid.ns=http://specs.openid.net/auth/2.0&openid.mode=checkid_setup&openid.return_to=${returnUrl}&openid.realm=${encodeURIComponent(window.location.origin)}&openid.identity=http://specs.openid.net/auth/2.0/identifier_select&openid.claimed_id=http://specs.openid.net/auth/2.0/identifier_select`;
    window.location.href = steamUrl;
  };

  // Logout do usuário
  const logout = () => {
    handleSetToken(null);

    navigate("/auth");
    window.location.reload();
  };

  return {
    isAuthenticated,
    isLoadingAuth,
    loginSocialGoogle,
    loginSocialSteam,
    normalLogin,
    handleLoginSteam,
    submiteRegister,
    submiteChangePassword,
    submiterResetPassword,
    logout,
    handleSetToken,
  };
};

export default useAuth;
